import { ProcessingEngine } from '../../engine.model';
import { OperationDescriptor } from '../../operation.model';
import { ProcessingImage } from '../../processing-image';

let cv: any = null;

// CDN URL for OpenCV.js (browser-ready, no Node deps)
const OPENCV_CDN_URL = 'https://docs.opencv.org/4.9.0/opencv.js';

/**
 * OpenCV.js processing engine.
 * Provides classic image processing operations: filters, morphology, edge detection, etc.
 * OpenCV is loaded from CDN on first use (no bundling of Node-dependent WASM).
 */
export class OpenCVEngine implements ProcessingEngine {
  readonly id = 'opencv';
  readonly name = 'OpenCV';
  readonly operations: OperationDescriptor[] = [
    {
      id: 'median-filter',
      name: 'Median Filter',
      engineId: 'opencv',
      category: 'filter',
      description: 'Applies a median blur to reduce noise while preserving edges',
      runsOn: 'client',
      params: [
        { id: 'ksize', label: 'Kernel Size', type: 'number', defaultValue: 5, min: 3, max: 31, step: 2 },
      ],
    },
    {
      id: 'gaussian-blur',
      name: 'Gaussian Blur',
      engineId: 'opencv',
      category: 'filter',
      description: 'Applies Gaussian smoothing to the image',
      runsOn: 'client',
      params: [
        { id: 'ksize', label: 'Kernel Size', type: 'number', defaultValue: 5, min: 1, max: 31, step: 2 },
        { id: 'sigmaX', label: 'Sigma X', type: 'number', defaultValue: 0, min: 0, max: 20, step: 0.5 },
      ],
    },
    {
      id: 'bilateral-filter',
      name: 'Bilateral Filter',
      engineId: 'opencv',
      category: 'filter',
      description: 'Edge-preserving smoothing filter',
      runsOn: 'client',
      params: [
        { id: 'd', label: 'Diameter', type: 'number', defaultValue: 9, min: 1, max: 25, step: 2 },
        { id: 'sigmaColor', label: 'Sigma Color', type: 'number', defaultValue: 75, min: 1, max: 200, step: 5 },
        { id: 'sigmaSpace', label: 'Sigma Space', type: 'number', defaultValue: 75, min: 1, max: 200, step: 5 },
      ],
    },
    {
      id: 'threshold',
      name: 'Threshold',
      engineId: 'opencv',
      category: 'filter',
      description: 'Applies binary thresholding to the image',
      runsOn: 'client',
      params: [
        { id: 'thresh', label: 'Threshold', type: 'number', defaultValue: 128, min: 0, max: 255, step: 1 },
        { id: 'maxval', label: 'Max Value', type: 'number', defaultValue: 255, min: 0, max: 255, step: 1 },
        {
          id: 'type', label: 'Type', type: 'select', defaultValue: 0,
          options: [
            { label: 'Binary', value: 0 },
            { label: 'Binary Inv', value: 1 },
            { label: 'Truncate', value: 2 },
            { label: 'To Zero', value: 3 },
            { label: 'To Zero Inv', value: 4 },
            { label: 'Otsu', value: 8 },
          ],
        },
      ],
    },
    {
      id: 'canny',
      name: 'Canny Edge Detection',
      engineId: 'opencv',
      category: 'detection',
      description: 'Detects edges using the Canny algorithm',
      runsOn: 'client',
      params: [
        { id: 'threshold1', label: 'Low Threshold', type: 'number', defaultValue: 50, min: 0, max: 255, step: 1 },
        { id: 'threshold2', label: 'High Threshold', type: 'number', defaultValue: 150, min: 0, max: 255, step: 1 },
        { id: 'apertureSize', label: 'Aperture', type: 'number', defaultValue: 3, min: 3, max: 7, step: 2 },
      ],
    },
    {
      id: 'morphology-erode',
      name: 'Erode',
      engineId: 'opencv',
      category: 'transform',
      description: 'Erodes the image (shrinks bright regions)',
      runsOn: 'client',
      params: [
        { id: 'ksize', label: 'Kernel Size', type: 'number', defaultValue: 3, min: 1, max: 21, step: 2 },
        { id: 'iterations', label: 'Iterations', type: 'number', defaultValue: 1, min: 1, max: 10, step: 1 },
      ],
    },
    {
      id: 'morphology-dilate',
      name: 'Dilate',
      engineId: 'opencv',
      category: 'transform',
      description: 'Dilates the image (expands bright regions)',
      runsOn: 'client',
      params: [
        { id: 'ksize', label: 'Kernel Size', type: 'number', defaultValue: 3, min: 1, max: 21, step: 2 },
        { id: 'iterations', label: 'Iterations', type: 'number', defaultValue: 1, min: 1, max: 10, step: 1 },
      ],
    },
    {
      id: 'morphology-open',
      name: 'Morphological Open',
      engineId: 'opencv',
      category: 'transform',
      description: 'Opening: erosion followed by dilation (removes small objects)',
      runsOn: 'client',
      params: [
        { id: 'ksize', label: 'Kernel Size', type: 'number', defaultValue: 5, min: 1, max: 21, step: 2 },
      ],
    },
    {
      id: 'morphology-close',
      name: 'Morphological Close',
      engineId: 'opencv',
      category: 'transform',
      description: 'Closing: dilation followed by erosion (fills small holes)',
      runsOn: 'client',
      params: [
        { id: 'ksize', label: 'Kernel Size', type: 'number', defaultValue: 5, min: 1, max: 21, step: 2 },
      ],
    },
    {
      id: 'histogram-equalization',
      name: 'Histogram Equalization',
      engineId: 'opencv',
      category: 'enhancement',
      description: 'Enhances contrast by equalizing the histogram',
      runsOn: 'client',
      params: [],
    },
    {
      id: 'clahe',
      name: 'CLAHE',
      engineId: 'opencv',
      category: 'enhancement',
      description: 'Contrast Limited Adaptive Histogram Equalization',
      runsOn: 'client',
      params: [
        { id: 'clipLimit', label: 'Clip Limit', type: 'number', defaultValue: 4.0, min: 1, max: 40, step: 0.5 },
        { id: 'tileSize', label: 'Tile Size', type: 'number', defaultValue: 8, min: 2, max: 32, step: 1 },
      ],
    },
    {
      id: 'sobel',
      name: 'Sobel Edge',
      engineId: 'opencv',
      category: 'detection',
      description: 'Computes gradient magnitude using Sobel operator',
      runsOn: 'client',
      params: [
        { id: 'ksize', label: 'Kernel Size', type: 'number', defaultValue: 3, min: 1, max: 7, step: 2 },
      ],
    },
    {
      id: 'invert',
      name: 'Invert',
      engineId: 'opencv',
      category: 'transform',
      description: 'Inverts image colours (negative)',
      runsOn: 'client',
      params: [],
    },
    {
      id: 'laplacian',
      name: 'Laplacian Edge',
      engineId: 'opencv',
      category: 'detection',
      description: 'Computes Laplacian of the image for edge detection',
      runsOn: 'client',
      params: [
        { id: 'ksize', label: 'Kernel Size', type: 'number', defaultValue: 3, min: 1, max: 7, step: 2 },
      ],
    },
    {
      id: 'resize',
      name: 'Resize',
      engineId: 'opencv',
      category: 'transform',
      description: 'Resizes the image to the specified dimensions',
      runsOn: 'client',
      params: [
        { id: 'width', label: 'Width', type: 'number', defaultValue: 256, min: 1, max: 4096, step: 1 },
        { id: 'height', label: 'Height', type: 'number', defaultValue: 256, min: 1, max: 4096, step: 1 },
        {
          id: 'interpolation', label: 'Interpolation', type: 'select', defaultValue: 'linear',
          options: [
            { label: 'Nearest', value: 'nearest' },
            { label: 'Linear', value: 'linear' },
            { label: 'Cubic', value: 'cubic' },
            { label: 'Area', value: 'area' },
            { label: 'Lanczos4', value: 'lanczos4' },
          ],
        },
      ],
    },
    {
      id: 'rotate',
      name: 'Rotate',
      engineId: 'opencv',
      category: 'transform',
      description: 'Rotates the image by a given angle (degrees)',
      runsOn: 'client',
      params: [
        { id: 'angle', label: 'Angle (°)', type: 'number', defaultValue: 90, min: -360, max: 360, step: 1 },
      ],
    },
    {
      id: 'color-convert',
      name: 'Color Convert',
      engineId: 'opencv',
      category: 'transform',
      description: 'Converts the image to a different color space',
      runsOn: 'client',
      params: [
        {
          id: 'conversion', label: 'Conversion', type: 'select', defaultValue: 'grayscale',
          options: [
            { label: 'To Grayscale', value: 'grayscale' },
            { label: 'To HSV', value: 'hsv' },
            { label: 'To Lab', value: 'lab' },
          ],
        },
      ],
    },
  ];

  private initialized = false;

  isInitialized(): boolean {
    return this.initialized;
  }

  async initialize(): Promise<void> {
    if (this.initialized) return;
    // Load OpenCV.js from CDN via script tag
    await new Promise<void>((resolve, reject) => {
      if ((window as any).cv && (window as any).cv.Mat) {
        cv = (window as any).cv;
        this.initialized = true;
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = OPENCV_CDN_URL;
      script.async = true;
      script.onload = () => {
        // OpenCV.js sets window.cv but it may need to initialize WASM
        const checkReady = () => {
          if ((window as any).cv && (window as any).cv.Mat) {
            cv = (window as any).cv;
            this.initialized = true;
            resolve();
          } else if ((window as any).cv && (window as any).cv.onRuntimeInitialized !== undefined) {
            (window as any).cv.onRuntimeInitialized = () => {
              cv = (window as any).cv;
              this.initialized = true;
              resolve();
            };
          } else {
            setTimeout(checkReady, 100);
          }
        };
        checkReady();
      };
      script.onerror = () => reject(new Error('Failed to load OpenCV.js from CDN'));
      document.head.appendChild(script);
    });
  }

  async execute(
    operation: OperationDescriptor,
    input: ProcessingImage,
    params: Record<string, any>
  ): Promise<ProcessingImage> {
    if (!this.initialized) await this.initialize();

    const src = this.toMat(input);
    let dst: any;

    try {
      dst = new cv.Mat();
      switch (operation.id) {
        case 'median-filter':
          this.medianFilter(src, dst, params);
          break;
        case 'gaussian-blur':
          this.gaussianBlur(src, dst, params);
          break;
        case 'bilateral-filter':
          this.bilateralFilter(src, dst, params);
          break;
        case 'threshold':
          this.threshold(src, dst, params);
          break;
        case 'canny':
          this.canny(src, dst, params);
          break;
        case 'morphology-erode':
          this.erode(src, dst, params);
          break;
        case 'morphology-dilate':
          this.dilate(src, dst, params);
          break;
        case 'morphology-open':
          this.morphologyOp(src, dst, params, cv.MORPH_OPEN);
          break;
        case 'morphology-close':
          this.morphologyOp(src, dst, params, cv.MORPH_CLOSE);
          break;
        case 'histogram-equalization':
          this.histogramEqualization(src, dst);
          break;
        case 'clahe':
          this.clahe(src, dst, params);
          break;
        case 'sobel':
          this.sobel(src, dst, params);
          break;
        case 'invert':
          cv.bitwise_not(src, dst);
          break;
        case 'laplacian':
          this.laplacian(src, dst, params);
          break;
        case 'resize':
          dst.delete();
          dst = this.resizeImage(src, params);
          break;
        case 'rotate':
          dst.delete();
          dst = this.rotateImage(src, params);
          break;
        case 'color-convert':
          this.colorConvert(src, dst, params);
          break;
        default:
          throw new Error(`Unknown OpenCV operation: ${operation.id}`);
      }
      return this.fromMat(dst);
    } finally {
      src.delete();
      if (dst) dst.delete();
    }
  }

  dispose(): void {
    // OpenCV WASM memory is managed per-Mat; nothing global to release.
  }

  // --- Operation implementations ---

  private medianFilter(src: any, dst: any, params: Record<string, any>): void {
    const gray = this.ensureGray(src);
    cv.medianBlur(gray, dst, params.ksize | 1);
    if (gray !== src) gray.delete();
  }

  private gaussianBlur(src: any, dst: any, params: Record<string, any>): void {
    const ksize = params.ksize | 1;
    cv.GaussianBlur(src, dst, new cv.Size(ksize, ksize), params.sigmaX || 0);
  }

  private bilateralFilter(src: any, dst: any, params: Record<string, any>): void {
    const gray = this.ensureGray(src);
    cv.bilateralFilter(gray, dst, params.d, params.sigmaColor, params.sigmaSpace);
    if (gray !== src) gray.delete();
  }

  private threshold(src: any, dst: any, params: Record<string, any>): void {
    const gray = this.ensureGray(src);
    cv.threshold(gray, dst, params.thresh, params.maxval, params.type);
    if (gray !== src) gray.delete();
  }

  private canny(src: any, dst: any, params: Record<string, any>): void {
    const gray = this.ensureGray(src);
    cv.Canny(gray, dst, params.threshold1, params.threshold2, params.apertureSize);
    if (gray !== src) gray.delete();
  }

  private erode(src: any, dst: any, params: Record<string, any>): void {
    const ksize = params.ksize | 1;
    const kernel = cv.getStructuringElement(cv.MORPH_RECT, new cv.Size(ksize, ksize));
    const anchor = new cv.Point(-1, -1);
    cv.erode(src, dst, kernel, anchor, params.iterations || 1);
    kernel.delete();
  }

  private dilate(src: any, dst: any, params: Record<string, any>): void {
    const ksize = params.ksize | 1;
    const kernel = cv.getStructuringElement(cv.MORPH_RECT, new cv.Size(ksize, ksize));
    const anchor = new cv.Point(-1, -1);
    cv.dilate(src, dst, kernel, anchor, params.iterations || 1);
    kernel.delete();
  }

  private morphologyOp(src: any, dst: any, params: Record<string, any>, op: number): void {
    const ksize = params.ksize | 1;
    const kernel = cv.getStructuringElement(cv.MORPH_RECT, new cv.Size(ksize, ksize));
    cv.morphologyEx(src, dst, op, kernel);
    kernel.delete();
  }

  private histogramEqualization(src: any, dst: any): void {
    const gray = this.ensureGray(src);
    cv.equalizeHist(gray, dst);
    if (gray !== src) gray.delete();
  }

  private clahe(src: any, dst: any, params: Record<string, any>): void {
    const gray = this.ensureGray(src);
    const tileSize = params.tileSize || 8;
    const clahe = new cv.CLAHE(params.clipLimit || 4.0, new cv.Size(tileSize, tileSize));
    clahe.apply(gray, dst);
    clahe.delete();
    if (gray !== src) gray.delete();
  }

  private sobel(src: any, dst: any, params: Record<string, any>): void {
    const gray = this.ensureGray(src);
    const gradX = new cv.Mat();
    const gradY = new cv.Mat();
    const absGradX = new cv.Mat();
    const absGradY = new cv.Mat();
    const ksize = params.ksize || 3;

    cv.Sobel(gray, gradX, cv.CV_16S, 1, 0, ksize);
    cv.Sobel(gray, gradY, cv.CV_16S, 0, 1, ksize);
    cv.convertScaleAbs(gradX, absGradX);
    cv.convertScaleAbs(gradY, absGradY);
    cv.addWeighted(absGradX, 0.5, absGradY, 0.5, 0, dst);

    gradX.delete();
    gradY.delete();
    absGradX.delete();
    absGradY.delete();
    if (gray !== src) gray.delete();
  }

  private laplacian(src: any, dst: any, params: Record<string, any>): void {
    const gray = this.ensureGray(src);
    const ksize = params.ksize || 3;
    const lap = new cv.Mat();
    cv.Laplacian(gray, lap, cv.CV_64F, ksize);
    cv.convertScaleAbs(lap, dst);
    lap.delete();
    if (gray !== src) gray.delete();
  }

  private resizeImage(src: any, params: Record<string, any>): any {
    const width = params.width || src.cols;
    const height = params.height || src.rows;
    const interpMap: Record<string, number> = {
      nearest: cv.INTER_NEAREST,
      linear: cv.INTER_LINEAR,
      cubic: cv.INTER_CUBIC,
      area: cv.INTER_AREA,
      lanczos4: cv.INTER_LANCZOS4,
    };
    const interp = interpMap[params.interpolation] ?? cv.INTER_LINEAR;
    const dst = new cv.Mat();
    cv.resize(src, dst, new cv.Size(width, height), 0, 0, interp);
    return dst;
  }

  private rotateImage(src: any, params: Record<string, any>): any {
    const angle = params.angle ?? 90;
    const center = new cv.Point(src.cols / 2, src.rows / 2);
    const matrix = cv.getRotationMatrix2D(center, angle, 1.0);
    const dst = new cv.Mat();
    cv.warpAffine(src, dst, matrix, new cv.Size(src.cols, src.rows));
    matrix.delete();
    return dst;
  }

  private colorConvert(src: any, dst: any, params: Record<string, any>): void {
    const conversion = params.conversion || 'grayscale';
    switch (conversion) {
      case 'grayscale': {
        const gray = this.ensureGray(src);
        gray.copyTo(dst);
        if (gray !== src) gray.delete();
        break;
      }
      case 'hsv': {
        const bgr = new cv.Mat();
        cv.cvtColor(src, bgr, cv.COLOR_RGBA2RGB);
        cv.cvtColor(bgr, dst, cv.COLOR_RGB2HSV);
        bgr.delete();
        break;
      }
      case 'lab': {
        const bgr = new cv.Mat();
        cv.cvtColor(src, bgr, cv.COLOR_RGBA2RGB);
        cv.cvtColor(bgr, dst, cv.COLOR_RGB2Lab);
        bgr.delete();
        break;
      }
      default:
        src.copyTo(dst);
    }
  }

  // --- Helpers ---

  private toMat(image: ProcessingImage): any {
    const mat = new cv.Mat(image.height, image.width, cv.CV_8UC4);
    if (image.channels === 4) {
      mat.data.set(image.data);
    } else if (image.channels === 1) {
      const rgba = new Uint8ClampedArray(image.width * image.height * 4);
      for (let i = 0; i < image.width * image.height; i++) {
        rgba[i * 4] = image.data[i];
        rgba[i * 4 + 1] = image.data[i];
        rgba[i * 4 + 2] = image.data[i];
        rgba[i * 4 + 3] = 255;
      }
      mat.data.set(rgba);
    } else if (image.channels === 3) {
      const rgba = new Uint8ClampedArray(image.width * image.height * 4);
      for (let i = 0; i < image.width * image.height; i++) {
        rgba[i * 4] = image.data[i * 3];
        rgba[i * 4 + 1] = image.data[i * 3 + 1];
        rgba[i * 4 + 2] = image.data[i * 3 + 2];
        rgba[i * 4 + 3] = 255;
      }
      mat.data.set(rgba);
    }
    return mat;
  }

  private fromMat(mat: any): ProcessingImage {
    let channels: number;
    let data: Uint8ClampedArray;

    if (mat.channels() === 1) {
      channels = 1;
      data = new Uint8ClampedArray(mat.data);
    } else if (mat.channels() === 4) {
      channels = 4;
      data = new Uint8ClampedArray(mat.data);
    } else {
      const rgba = new cv.Mat();
      cv.cvtColor(mat, rgba, cv.COLOR_BGR2RGBA);
      channels = 4;
      data = new Uint8ClampedArray(rgba.data);
      rgba.delete();
    }

    return new ProcessingImage(mat.cols, mat.rows, channels, data);
  }

  private ensureGray(src: any): any {
    if (src.channels() === 1) return src;
    const gray = new cv.Mat();
    cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);
    return gray;
  }

}
